import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const BookRavi = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    eventName: '',
    eventDate: '',
    eventLocation: '',
    audienceType: '',
    audienceSize: '',
    eventFormat: 'keynote',
    goals: '',
    budgetRange: '',
    additionalDetails: '',
    bookSigningInterest: false,
    website: '' // Honeypot field - should remain empty
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Honeypot spam protection - if 'website' field is filled, it's a bot
      if (formData.website) {
        // Silently reject but show success to bot
        setTimeout(() => {
          setSubmitted(true);
          setIsSubmitting(false);
        }, 1500);
        return;
      }
      
      // Get the Google Apps Script URL from environment variable
      const scriptUrl = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
      
      if (!scriptUrl) {
        alert('Form configuration error. Please contact support.');
        setIsSubmitting(false);
        return;
      }
      
      // Remove honeypot field before sending
      const { website, ...cleanData } = formData;
      
      // Send form data to Google Apps Script
      const response = await fetch(scriptUrl, {
        method: 'POST',
        mode: 'no-cors', // Google Apps Script requires no-cors mode
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanData)
      });
      
      // no-cors mode doesn't allow reading response, so we assume success if no error thrown
      setSubmitted(true);
      setIsSubmitting(false);
      
    } catch (error) {
      alert('There was an error submitting your inquiry. Please try again or contact us directly at raviprakash@yoursafar.com');
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-soft-stone flex items-center justify-center px-4">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-12 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h2 className="heading-md text-charcoal mb-4">
            Thank you for your inquiry!
          </h2>
          <p className="text-lg font-sans text-gray-700 mb-8">
            We've received your booking request and will respond within 1–2 business days.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                firstName: '',
                lastName: '',
                email: '',
                organization: '',
                eventName: '',
                eventDate: '',
                eventLocation: '',
                audienceType: '',
                audienceSize: '',
                eventFormat: 'keynote',
                goals: '',
                budgetRange: '',
                additionalDetails: '',
                bookSigningInterest: false,
                website: '' // Reset honeypot
              });
            }}
            className="btn-primary"
          >
            Submit Another Inquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-soft-stone">
      <SEO 
        title="Book Ravi - Speaking Inquiry & Event Booking"
        description="Book Ravi Prakash for your keynote event, conference, or corporate retreat. TEDx speaker with 59K+ views specializing in leadership, resilience, and reinvention. Check availability and get a custom proposal."
        canonical="/book-ravi"
      />

      {/* Hero Section */}
      <section className="bg-safar-blue text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="heading-xl text-white mb-4">
            Book Ravi for your event
          </h1>
          <p className="text-lg font-sans text-gray-100">
            For keynote talks, fireside chats, panels, graduations, author talks, and book signing events.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-8 shadow-md sticky top-24">
              <h2 className="text-2xl font-serif font-semibold text-charcoal mb-4">
                Who this is for
              </h2>
              
              <p className="text-base font-sans text-gray-700 mb-6 leading-relaxed">
                This page is for:
              </p>
              
              <ul className="space-y-3 mb-8">
                {[
                  'Conference organizers',
                  'Corporate event planners',
                  'Leadership retreat hosts',
                  'Universities and graduation planners',
                  'Panel and fireside chat organizers',
                  'Teams planning author talks or book signing events'
                ].map((item) => (
                  <li key={item} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-safar-blue mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-sans text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="border-t border-gray-200 pt-6">
                <p className="text-sm font-sans text-gray-600 italic">
                  If your audience is navigating pressure, change, identity, growth, or the human side of leadership, Ravi may be a strong fit.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg p-8 shadow-md">
              <h2 className="text-2xl font-serif font-semibold text-charcoal mb-2">
                Tell us about your event
              </h2>
              <p className="text-sm font-sans text-gray-600 mb-8">
                The more context you share, the faster we can determine fit and respond meaningfully.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from humans, visible to bots */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                  <label htmlFor="website">Website (leave blank)</label>
                  <input
                    type="text"
                    name="website"
                    id="website"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                      First name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                      Last name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                  />
                </div>

                {/* Organization */}
                <div>
                  <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                    Organization *
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                  />
                </div>

                {/* Event Name */}
                <div>
                  <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                    Event name *
                  </label>
                  <input
                    type="text"
                    name="eventName"
                    value={formData.eventName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                  />
                </div>

                {/* Event Date and Location */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                      Event date *
                    </label>
                    <input
                      type="date"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                      Event location *
                    </label>
                    <input
                      type="text"
                      name="eventLocation"
                      value={formData.eventLocation}
                      onChange={handleChange}
                      required
                      placeholder="City, State or Virtual"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                    />
                  </div>
                </div>

                {/* Audience Type and Size */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                      Audience type *
                    </label>
                    <input
                      type="text"
                      name="audienceType"
                      value={formData.audienceType}
                      onChange={handleChange}
                      required
                      placeholder="e.g., Corporate leaders, Students, etc."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                      Estimated audience size *
                    </label>
                    <input
                      type="text"
                      name="audienceSize"
                      value={formData.audienceSize}
                      onChange={handleChange}
                      required
                      placeholder="e.g., 100-200"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                    />
                  </div>
                </div>

                {/* Event Format */}
                <div>
                  <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                    Event format *
                  </label>
                  <select
                    name="eventFormat"
                    value={formData.eventFormat}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                  >
                    <option value="keynote">Keynote</option>
                    <option value="fireside-chat">Fireside chat</option>
                    <option value="panel">Panel</option>
                    <option value="graduation">Graduation</option>
                    <option value="author-talk">Author talk</option>
                    <option value="book-signing">Book signing</option>
                  </select>
                </div>

                {/* Goals */}
                <div>
                  <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                    What do you want the audience to leave with? *
                  </label>
                  <textarea
                    name="goals"
                    value={formData.goals}
                    onChange={handleChange}
                    required
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                  ></textarea>
                </div>

                {/* Budget Range */}
                <div>
                  <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                    Budget range (optional)
                  </label>
                  <input
                    type="text"
                    name="budgetRange"
                    value={formData.budgetRange}
                    onChange={handleChange}
                    placeholder="e.g., $10,000 - $15,000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                  />
                </div>

                {/* Additional Details */}
                <div>
                  <label className="block text-sm font-sans font-semibold text-charcoal mb-2">
                    Additional details
                  </label>
                  <textarea
                    name="additionalDetails"
                    value={formData.additionalDetails}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Any other information that would help us understand your event better"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safar-blue focus:border-transparent font-sans"
                  ></textarea>
                </div>

                {/* Book Signing Interest */}
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    name="bookSigningInterest"
                    checked={formData.bookSigningInterest}
                    onChange={handleChange}
                    className="mt-1 h-5 w-5 text-safar-blue border-gray-300 rounded focus:ring-safar-blue"
                  />
                  <label className="ml-3 text-sm font-sans text-gray-700">
                    I'd also like information about book signing or author event options
                  </label>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary text-lg py-4 flex items-center justify-center disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        Submit Booking Inquiry
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </button>
                </div>

                {/* Footer Note */}
                <div className="bg-soft-stone rounded-lg p-4 text-center">
                  <p className="text-sm font-sans text-gray-600">
                    We aim to respond within 1–2 business days.
                  </p>
                  <p className="text-sm font-sans text-gray-600">
                    In-person and virtual opportunities available.
                  </p>
                  <p className="text-sm font-sans text-gray-600 mt-2">
                    For media requests, use the same form and note "media inquiry."
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookRavi;
